import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Star } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Import components
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import ActionsView from './components/ActionsView';
import RewardsView from './components/RewardsView';
import ProfileView from './components/ProfileView';
import Navigation from './components/Navigation';
import RegisterScreen from './components/RegisterScreen';
import AppLogo from './components/AppLogo';
import CommunityFeedView from './components/CommunityFeedView';
import CommunityPostsAdminView from './components/CommunityPostsAdminView';
import AppFooter from './components/AppFooter';
import Terms from './pages/Terms';

import {
  supabase,
  configureAuthPersistence,
  isSupabaseConfigured,
  supabaseConfigErrorMessage,
} from './helper/supabaseClient';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { usePwaInstallContext } from './hooks/usePwaInstallContext';
import InstallBanner from './components/InstallBanner';

import { compressImage } from './helper/imageCompression';
import { getWeeklyTierStats } from './utils/tiers';

/** Maps `public."Profiles"` row onto user state for country / community. */
function communityFieldsFromProfile(profile) {
  if (!profile) return { country: null, communityId: null };
  return {
    country: profile.country ?? null,
    communityId: profile.community_id ?? null,
  };
}

/**
 * True when this Supabase session is from Google OAuth (do not rely on localStorage flags).
 * Checks app_metadata.provider, app_metadata.providers, and identities[].provider.
 */
function isGoogleOAuthSession(session) {
  const user = session?.user;
  if (!user) return false;

  const meta = user.app_metadata || {};
  if (meta.provider === 'google') return true;

  const providers = meta.providers;
  if (Array.isArray(providers) && providers.some((p) => String(p).toLowerCase() === 'google')) {
    return true;
  }

  const identities = user.identities;
  if (Array.isArray(identities) && identities.some((i) => i?.provider === 'google')) {
    return true;
  }

  return false;
}

const App = () => {
  // State management
  const [currentView, setCurrentView] = useState('login');
  const [authChecked, setAuthChecked] = useState(false);
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const [userActions, setUserActions] = useState([]);
  const [userRewards, setUserRewards] = useState([]);
  const { isInstallAvailable, promptInstall, hasInstalled } = useInstallPrompt();
  const installCtx = usePwaInstallContext({
    isInstallPromptAvailable: isInstallAvailable,
    hasInstalledEvent: hasInstalled,
  });

  /** Prevents duplicate Google completion (e.g. INITIAL_SESSION + SIGNED_IN) from racing inserts. */
  const googleOAuthHandledRef = useRef(false);

  /** Lets session-restore call the same Google completion logic (defined in a later effect). */
  const completeGoogleSessionRef = useRef(async (_session) => {});

  const tierStats = useMemo(() => getWeeklyTierStats(userActions), [userActions]);

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user;
      const meta = u?.app_metadata;
      console.log(
        '[auth] onAuthStateChange:',
        event,
        'session?',
        !!session,
        'userId:',
        u?.id ?? null,
        'app_metadata.provider:',
        meta?.provider ?? null,
        'app_metadata.providers:',
        meta?.providers ?? null,
        'isGoogleOAuthSession:',
        isGoogleOAuthSession(session)
      );
      if (event === 'SIGNED_OUT') {
        console.log('[auth] SIGNED_OUT');
      }
      if (event === 'TOKEN_REFRESHED') {
        console.log('[auth] TOKEN_REFRESHED');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleGoogleLogin = async (keepSignedIn = true) => {
    console.log('[Google OAuth] start signInWithOAuth, keepSignedIn:', keepSignedIn);
    setMessage('');
    configureAuthPersistence(keepSignedIn);

    const base =
      (typeof process !== 'undefined' && process.env.REACT_APP_OAUTH_REDIRECT_URL) ||
      `${window.location.origin}/`;
    const redirectTo = base.endsWith('/') ? base : `${base}/`;
    console.log('[Google OAuth] redirectTo:', redirectTo, '(set REACT_APP_OAUTH_REDIRECT_URL to override)');

    localStorage.setItem('isGoogleSignIn', 'true');
    googleOAuthHandledRef.current = false;

    const { data: oauthData, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: false,
      },
    });

    if (error) {
      console.error('[Google OAuth] signInWithOAuth error:', error);
      setMessage(error.message);
      localStorage.removeItem('isGoogleSignIn');
      return;
    }
    console.log('[Google OAuth] redirecting to provider, url:', oauthData?.url ?? '(none)');
  };

  const fetchUserInfo = useCallback(async (id) => {
    const { data: actionData, error: actionError } = await supabase
      .from('actions_log')
      .select('*')
      .eq('user_id', id)
      .order('created_at', { ascending: false });

    if (actionError) {
      console.error('Error fetching user actions:', actionError);
      return;
    }

    const { data: rewardData, error: rewardError } = await supabase
      .from('rewards_log')
      .select('*')
      .eq('user_id', id);

    if (rewardError) {
      console.error('Error fetching user rewards:', rewardError);
      return;
    }

    setUserActions(actionData);
    setUserRewards(rewardData);
  }, []);

  const applyAuthUserToAppState = useCallback(async (authUser) => {
    const { data: DBData, error: profileError } = await supabase
      .from('Profiles')
      .select('*')
      .eq('id', authUser.id)
      .maybeSingle();

    console.log('[Profiles] row:', DBData);

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      setMessage('Error fetching user profile');
      return false;
    }

    if (DBData) {
      const userData = {
        id: authUser.id,
        email: authUser.email,
        name: DBData.name,
        joinDate: DBData.join_date,
        weeklyTierFromProfile: DBData.weekly_tier ?? null,
        role: DBData.role ?? 'user',
        ...communityFieldsFromProfile(DBData),
      };
      console.log('[Profiles] final role:', userData.role);
      setUser(userData);
      setCurrentView('dashboard');
      fetchUserInfo(userData.id);
    } else {
      const userData = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.email?.split('@')?.[0] || 'User',
        weeklyTierFromProfile: null,
        role: 'user',
        ...communityFieldsFromProfile(null),
      };
      console.log('[Profiles] missing row; default role:', userData.role);
      setUser(userData);
      setCurrentView('dashboard');
      fetchUserInfo(userData.id);
    }
    return true;
  }, [fetchUserInfo]);

  /**
   * Finishes Google OAuth using the session from the auth callback when possible.
   * Root cause fix: after redirect, `getSession()` can lag behind the session passed to
   * `onAuthStateChange`, so we must use the callback session.
   *
   * Also: do not treat `providers.includes('email')` as "email-password only" when `google`
   * is present — Supabase often lists both for Google users, which previously signed them out.
   */
  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;

    const clearGoogleIntent = () => {
      console.log('[Google OAuth] clear isGoogleSignIn');
      localStorage.removeItem('isGoogleSignIn');
    };

    const completeGoogleSession = async (sessionFromEvent) => {
      const { data: getSessionData } = await supabase.auth.getSession();
      const session = sessionFromEvent ?? getSessionData.session;

      console.log('[Google OAuth] completeGoogleSession enter', {
        hasUser: !!session?.user,
        usedEventSession: !!sessionFromEvent,
      });

      if (!session?.user) {
        console.log('[Google OAuth] no session user — abort');
        return;
      }

      const viaMetadata = isGoogleOAuthSession(session);
      const viaIntentFlag =
        typeof localStorage !== 'undefined' && localStorage.getItem('isGoogleSignIn') === 'true';
      console.log('[Google OAuth] should complete Google profile flow?', {
        viaMetadata,
        viaIntentFlag,
        app_provider: session.user.app_metadata?.provider ?? null,
        app_providers: session.user.app_metadata?.providers ?? null,
        identityProviders: session.user.identities?.map((i) => i?.provider) ?? null,
      });

      if (!viaMetadata && !viaIntentFlag) {
        console.log(
          '[Google OAuth] skip completeGoogleSession: not Google (metadata) and no OAuth intent flag'
        );
        return;
      }

      if (googleOAuthHandledRef.current) {
        console.log('[Google OAuth] completeGoogleSession skipped (already handled)');
        return;
      }
      googleOAuthHandledRef.current = true;

      const user = session.user;
      console.log('[Google OAuth] proceeding — user id:', user.id, 'email:', user.email);
      const providers = user.app_metadata?.providers || [];
      console.log('[Google OAuth] app_metadata.providers:', providers);

      const { data: existingProfile, error: profileError } = await supabase
        .from('Profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      console.log('[Google OAuth] profile fetch by id:', {
        found: !!existingProfile,
        error: profileError?.message ?? null,
      });

      if (profileError) {
        console.error('[Google OAuth] profile query error:', profileError);
        googleOAuthHandledRef.current = false;
        setMessage('Error checking user profile');
        clearGoogleIntent();
        return;
      }

      if (existingProfile) {
        const hasEmailPasswordOnly =
          providers.includes('email') && !providers.includes('google');

        if (hasEmailPasswordOnly) {
          console.warn('[Google OAuth] blocking: email-password account without google provider');
          googleOAuthHandledRef.current = false;
          await supabase.auth.signOut();
          setMessage('An account with this email already exists. Please login with your email and password.');
          clearGoogleIntent();
          return;
        }

        const userData = {
          id: user.id,
          email: user.email,
          name: existingProfile.name,
          joinDate: existingProfile.join_date,
          weeklyTierFromProfile: existingProfile.weekly_tier ?? null,
          role: existingProfile.role ?? 'user',
          ...communityFieldsFromProfile(existingProfile),
        };
        console.log('[Google OAuth] signed in with existing profile, role:', userData.role);
        setUser(userData);
        setCurrentView('dashboard');
        fetchUserInfo(userData.id);
        clearGoogleIntent();
        console.log('[Google OAuth] navigation: dashboard (existing profile)');
      } else {
        const { data: emailCheck, error: emailCheckError } = await supabase
          .from('Profiles')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();

        console.log('[Google OAuth] email collision check:', {
          conflict: !!emailCheck,
          error: emailCheckError?.message ?? null,
        });

        if (emailCheck) {
          googleOAuthHandledRef.current = false;
          await supabase.auth.signOut();
          setMessage('An account with this email already exists. Please login with your email and password.');
          clearGoogleIntent();
          return;
        }

        const userData = {
          id: user.id,
          email: user.email,
          name: user.user_metadata?.full_name || user.email.split('@')[0],
          weeklyTierFromProfile: null,
          role: 'user',
          ...communityFieldsFromProfile(null),
        };

        const { error: insertError } = await supabase
          .from('Profiles')
          .insert([
            {
              id: userData.id,
              email: userData.email,
              name: userData.name,
              total_points: 0,
              role: 'user',
              marketing_consent: false,
            },
          ]);

        console.log('[Google OAuth] profile insert:', { ok: !insertError, error: insertError?.message ?? null });

        if (insertError) {
          console.error('[Google OAuth] profile insert error:', insertError);
          googleOAuthHandledRef.current = false;
          setMessage('Error creating user profile');
          await supabase.auth.signOut();
          clearGoogleIntent();
          return;
        }

        console.log('[Google OAuth] new profile created, hydrating dashboard');
        setUser(userData);
        setCurrentView('dashboard');
        fetchUserInfo(userData.id);
        clearGoogleIntent();
        console.log('[Google OAuth] navigation: dashboard (new profile)');
      }
    };

    completeGoogleSessionRef.current = completeGoogleSession;

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Google OAuth] listener event:', event, 'session?', !!session?.user);
      if (!session?.user) return;
      if (event !== 'SIGNED_IN' && event !== 'INITIAL_SESSION') return;

      const viaMeta = isGoogleOAuthSession(session);
      const viaFlag =
        typeof localStorage !== 'undefined' && localStorage.getItem('isGoogleSignIn') === 'true';
      console.log('[Google OAuth] listener branch check:', { viaMeta, viaFlag, event });

      if (!viaMeta && !viaFlag) return;

      void completeGoogleSession(session);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [fetchUserInfo]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setAuthChecked(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      const viaFlag =
        typeof localStorage !== 'undefined' && localStorage.getItem('isGoogleSignIn') === 'true';
      const viaMeta = session ? isGoogleOAuthSession(session) : false;
      console.log(
        '[auth] session restore:',
        'session?',
        !!session?.user,
        'isGoogleOAuthSession?',
        viaMeta,
        'isGoogleIntentFlag?',
        viaFlag,
        'app_metadata.provider:',
        session?.user?.app_metadata?.provider ?? null,
        'href has auth fragment?',
        typeof window !== 'undefined' &&
          /(#|&)access_token=|(#|&)code=/.test(window.location.hash + window.location.search)
      );
      if (session?.user) {
        if (viaMeta || viaFlag) {
          console.log('[auth] restore → completeGoogleSession (Google OAuth)');
          await completeGoogleSessionRef.current(session);
        } else {
          console.log('[auth] restore → applyAuthUserToAppState (non-Google) user id:', session.user.id);
          await applyAuthUserToAppState(session.user);
        }
      }
      if (!cancelled) setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [applyAuthUserToAppState]);

  const handleLogin = async (email, password, keepSignedIn = true) => {
    localStorage.removeItem('isGoogleSignIn');
    setMessage('');
    configureAuthPersistence(keepSignedIn);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    console.log('[auth] user id:', data.user.id);
    await applyAuthUserToAppState(data.user);
  };

  const handleRegister = async (email, password, country, communityId, marketingConsent = false) => {

    localStorage.removeItem('isGoogleSignIn');

    setMessage('');
    configureAuthPersistence(true);

    if (!country || !communityId) {
      setMessage('Please select your country and community.');
      return;
    }

    const { data, error } = await supabase.auth.signUp({ // Register user in Supabase Auth
      email: email,
      password: password
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    if (!data.user?.id) {
      setMessage('Check your email to confirm your account, then sign in.');
      return;
    }

    const userData = {
      id: data.user.id,
      email: email,
      name: email.split('@')[0],
      weeklyTierFromProfile: null,
      role: 'user',
      country,
      communityId,
    };

    const { error: profileError } = await supabase
      .from('Profiles')
      .insert([
        {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          total_points: 0,
          role: 'user',
          country,
          community_id: communityId,
          marketing_consent: Boolean(marketingConsent),
        },
      ])
      .single();
    if (profileError) {
      console.error('Error creating user profile:', profileError);
      setMessage(profileError.message || 'Error creating profile');
    } else {
      setUser(userData);
      setCurrentView('dashboard');
      fetchUserInfo(userData.id);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    localStorage.removeItem('isGoogleSignIn');
    if (error) {
      console.error('Error signing out:', error);
    }

    setUser(null);
    setUserActions([]);
    setUserRewards([]);

    setCurrentView('login');
  };

  /** Keeps in-memory `user` aligned with `public."Profiles"` after Profile saves country / community. */
  const handleProfileCommunityUpdate = (patch) => {
    setUser((u) => (u ? { ...u, ...patch } : null));
  };

  /**
   * @param {{ id: string, actionKey: string, title: string, isGenericPrompt: boolean, points?: number }} actionSnapshot
   */
  const logAction = async (actionSnapshot, method, photoFile = null, description = null) => {
    if (!actionSnapshot?.id) return;

    const { id: actionId, actionKey, title, isGenericPrompt, points: actionPoints = 0 } = actionSnapshot;

    const actionDescription =
      typeof description === 'string' && description.trim() !== ''
        ? description.trim()
        : null;

    if (isGenericPrompt) {
      if (!actionDescription) {
        setMessage('Please describe your climate action.');
        return;
      }
    } else if (method === 'text' && !actionDescription) {
      setMessage('Please enter a description.');
      return;
    }

    // Generic prompt actions should have a stable title in `actions_log.title`,
    // while the user's free text should be saved to `actions_log.description`.
    const logTitle = isGenericPrompt ? 'Generic climate action' : title;
    const logDescription = actionDescription;

    let photoUrl = null;

    // Upload photo to Supabase Storage if provided
    if (photoFile) {
      const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();

      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        setMessage('Your session expired. Please log in again.');
        handleLogout();
        return;
      }

      // Compress the image before uploading
      let fileToUpload = photoFile;
      try {
        fileToUpload = await compressImage(photoFile, 500); // 500KB max
        console.log('Original size:', (photoFile.size / 1024).toFixed(2), 'KB');
        console.log('Compressed size:', (fileToUpload.size / 1024).toFixed(2), 'KB');
      } catch (compressionError) {
        console.error('Error compressing image:', compressionError);
        // If compression fails, continue with original file
      }

      const fileName = `${user.id}/${actionId}_${uuidv4()}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('action_photos')
        .upload(filePath, fileToUpload);

      if (uploadError) {
        console.error('Error uploading photo:', uploadError);
        setMessage('Error uploading photo. Please try again.');
        return;
      }

      // Get URL for the uploaded photo
      const { data: urlData } = supabase.storage
        .from('action_photos')
        .getPublicUrl(filePath);

      photoUrl = urlData.publicUrl;
    }

    const newAction = {
      id: Date.now().toString(),
      action_id: actionId,
      action_key: actionKey || null,
      title: logTitle,
      points: actionPoints,
      created_at: new Date().toISOString(),
      method: method, // 'photo', 'text', or 'manual'
      photo_url: photoUrl,
      description: logDescription,
      verified: 0,
    };

    const row = {
      user_id: user.id,
      action_id: actionId,
      action_key: actionKey || null,
      title: logTitle,
      points: actionPoints,
      created_at: new Date().toISOString(),
      method,
      photo_url: photoUrl,
      description: logDescription,
      verified: 0,
    };

    const { error: logError } = await supabase.from('actions_log').insert([row]);

    if (logError) {
      console.error('Error logging action:', logError);
      return;
    };

    const updatedActions = [newAction, ...userActions];
    setUserActions(updatedActions);
  };

  // Main render logic
  return (
    <Routes>
      <Route path="/terms" element={<Terms />} />
      <Route
        path="*"
        element={
          <>
            {!authChecked ? (
              <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <p className="text-gray-600 text-sm">Loading…</p>
              </div>
            ) : null}
            {authChecked && !isSupabaseConfigured && (
              <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
                  <h2 className="font-semibold text-base mb-2">Supabase setup required</h2>
                  <p className="text-sm mb-2">{supabaseConfigErrorMessage}</p>
                  <p className="text-xs">
                    Create a <code>.env</code> file in the project root, then restart <code>npm start</code>.
                  </p>
                </div>
              </div>
            )}
            {authChecked && isSupabaseConfigured && currentView === 'login' && (
              <LoginScreen
                onLogin={handleLogin}
                onGoogleLogin={handleGoogleLogin}
                onShowRegister={() => {
                  setCurrentView('register');
                  setMessage('');
                }}
                message={message}
              />
            )}

            {authChecked && isSupabaseConfigured && currentView === 'register' && (
              <RegisterScreen
                onRegister={handleRegister}
                onShowLogin={() => {
                  setCurrentView('login');
                  setMessage('');
                }}
                message={message}
              />
            )}

            {authChecked &&
              isSupabaseConfigured &&
              currentView !== 'login' &&
              currentView !== 'register' && (
              <div className="min-h-screen bg-gray-50">
                <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
                  {/* Header */}
                  <div className="bg-white border-b border-gray-200 px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-2 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                          <AppLogo className="h-7 w-7 object-contain" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-bold text-lg">SustainabilityHub</h1>
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200/70">
                              Pilot
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">{tierStats.tierLabel}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isInstallAvailable && !installCtx.canShowBanner ? (
                          <button
                            type="button"
                            onClick={() => {
                              void promptInstall();
                            }}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                            aria-label="Install App"
                          >
                            Install App
                          </button>
                        ) : null}
                        <div className="p-2 bg-orange-100 rounded-full">
                          <Star className="h-4 w-4 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <InstallBanner
                    variant={installCtx.bannerVariant}
                    onInstall={() => {
                      void promptInstall();
                    }}
                    onDismiss={installCtx.dismiss}
                  />

                  {/* Main content */}
                  <div className="px-4 pt-6 pb-20">
                    {currentView === 'dashboard' && (
                      <Dashboard
                        user={user}
                        userActions={userActions}
                        userRewards={userRewards}
                        tierStats={tierStats}
                      />
                    )}
                    {currentView === 'actions' && (
                      <ActionsView
                        onLogAction={logAction}
                      />
                    )}
                    {currentView === 'feed' && (
                      <CommunityFeedView
                        user={user}
                        onManagePosts={() => setCurrentView('feed-admin')}
                      />
                    )}
                    {currentView === 'feed-admin' && (
                      <CommunityPostsAdminView
                        user={user}
                        onBack={() => setCurrentView('feed')}
                      />
                    )}
                    {currentView === 'rewards' && (
                      <RewardsView tierStats={tierStats} user={user} />
                    )}
                    {currentView === 'profile' && (
                      <ProfileView
                        user={user}
                        userActions={userActions}
                        userRewards={userRewards}
                        tierStats={tierStats}
                        onLogout={handleLogout}
                        onProfileCommunityUpdate={handleProfileCommunityUpdate}
                      />
                    )}
                    <AppFooter className="mt-10" />
                  </div>

                  {/* Navigation */}
                  <Navigation
                    currentView={currentView}
                    onViewChange={setCurrentView}
                  />
                </div>
              </div>
            )}
          </>
        }
      />
    </Routes>
  );
};

export default App;